import { Box, Button, Divider, Drawer, IconButton, Stack } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { Icon } from "../../components/Icon";
import { LinkBehavior } from "../../components/LinkBehavior";
import { generalMessages } from "../../components/messages";
import { useMsg } from "../../components/Msg/Msg";
import { ScheduledSessions } from "../../components/ScheduledSessions/ScheduledSessionsTableRow";
import { DateCell, TLCell, User, UserCell } from "../../components/Table";
import { TLTableWithHeader } from "../../components/Table/TLTableWithHeader";
import { H2, P } from "../../components/Typography";
import { routes } from "../../routes";
import { formatName } from "../Coaches/coach.utils";
import { ConfirmModal } from "../Modal/ConfirmModal";
import { SlotChip } from "../Team/Team.page";
import { AddClientModal } from "./AddClientModal";
import {
  useClientsQuery,
  useDeclineMutation,
  useUpcomingCoachSessionsQuery,
} from "./api";
import { ClientNotes } from "./ClientNotes";
import { clientsMessages } from "./messages";

export function ClientsTable() {
  const [addMemberVisible, setAddMemberVisible] = useState();
  const [declineMemberVisible, setDeclineMemberVisible] = useState();
  const [selectedClient, setSelectedClient] = useState();
  const msg = useMsg({ dict: clientsMessages });
  const unsavedRef = useRef(false);
  const [unsavedModalVisible, setUnsavedModalVisible] = useState(false);
  const handleCloseDrawer = () => {
    if (unsavedRef.current) {
      setUnsavedModalVisible(true);
    } else {
      setSelectedClient();
    }
  };
  const generalMsg = useMsg({ dict: generalMessages });
  ConfirmModal.useModal(
    useMemo(
      () => ({
        open: !!unsavedModalVisible,
        onClose: () => {
          setUnsavedModalVisible(false);
        },
        iconName: "DeleteOutlined",
        title: generalMsg("general.unsaved-changes.title"),
        desc: "",
        sx: { width: "800px" },
        buttons: [
          {
            variant: "contained",
            color: "error",
            type: "button",
            children: generalMsg("general.unsaved-changes.leave"),
            onClick: () => {
              unsavedRef.current = false; // reset unsaved state
              handleCloseDrawer();
              setUnsavedModalVisible(false);
            },
          },
          {
            variant: "contained",
            type: "button",
            children: generalMsg("general.unsaved-changes.close"),
            onClick: () => setUnsavedModalVisible(false),
          },
        ],
      }),
      [generalMsg, unsavedModalVisible],
    ),
  );

  const query = useClientsQuery();
  const upcomingSessionsQuery = useUpcomingCoachSessionsQuery();
  const declineMutation = useDeclineMutation({
    onSuccess: () => {
      setDeclineMemberVisible();
    },
  });

  const getUpcomingSessions = ({ username }) =>
    upcomingSessionsQuery.data?.filter((row) => row.username === username) ||
    [];

  const columns = [
    {
      label: msg("clients.table.col.name"),
      key: "name",
      render: (row) => (
        <UserCell
          email={row.username}
          name={formatName(row)}
          onClick={() => setSelectedClient(row)}
        />
      ),
    },
    {
      label: msg("clients.table.col.lastSession"),
      key: "lastSession",
      render: (row) => <DateCell utcStr={row.lastSession} />,
    },
    {
      label: msg("clients.table.col.nextSession"),
      key: "nextSession",
      render: (row) => <DateCell utcStr={row.nextSession} />,
    },
    {
      label: msg("clients.table.col.action"),
      key: "action",
      render: (row) => (
        <TLCell>
          <Button
            sx={{ bgcolor: "white" }}
            variant="outlined"
            component={LinkBehavior}
            href={routes.messages}
            state={{ messagesFrom: row.username }}
          >
            {msg("clients.table.action.contact")}
          </Button>
          <Button
            sx={{ ml: 2, bgcolor: "white" }}
            variant="outlined"
            component={LinkBehavior}
            color="error"
            onClick={() => {
              setDeclineMemberVisible(row);
            }}
          >
            {msg("clients.table.action.decline")}
          </Button>
        </TLCell>
      ),
    },
  ];
  ConfirmModal.useModal(
    useMemo(
      () => ({
        open: !!declineMemberVisible,
        onClose: () => setDeclineMemberVisible(),
        iconName: "RocketLaunch",
        title: msg("clients.decline.title", {
          name: declineMemberVisible?.name || declineMemberVisible?.username,
        }),
        desc: "",
        error: declineMutation.error,
        sx: { width: "800px" },
        buttons: [
          {
            variant: "outlined",
            type: "button",
            children: msg("clients.decline.no"),
            onClick: () => setDeclineMemberVisible(),
          },
          {
            component: Button,
            variant: "contained",
            color: "error",
            type: "button",
            children: msg("clients.decline.yes"),
            disabled: declineMutation.isPending,
            loading: declineMutation.isPending,
            onClick: () => declineMutation.mutate(declineMemberVisible),
          },
        ],
      }),
      [declineMemberVisible, declineMutation, msg],
    ),
  );

  console.log("[Clients.page]", { query, declineMemberVisible });

  return (
    <>
      <TLTableWithHeader
        title={
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              minHeight: 30,
            }}
          >
            <H2>{msg("clients.title")}</H2>
            <SlotChip
              loading={query.isFetching}
              sx={{ display: "inline-flex", p: 0.75, ml: 4 }}
            >
              {msg("clients.title.count.badge", {
                count: query.data?.length,
              })}
            </SlotChip>
          </Box>
        }
        subheader={<P mt={1.5}>{msg("clients.sub")}</P>}
        columns={columns}
        query={query}
        action={
          <Button
            variant="contained"
            startIcon={<Icon name="Add" />}
            aria-label="add member"
            onClick={() => {
              setAddMemberVisible(true);
            }}
          >
            {msg("clients.add")}
          </Button>
        }
        key="username"
        selectedKeys={selectedClient ? [selectedClient.username] : undefined}
      />
      <AddClientModal
        open={addMemberVisible}
        onClose={() => setAddMemberVisible(false)}
      />

      <Drawer
        open={!!selectedClient}
        onClose={handleCloseDrawer}
        anchor="right"
        sx={{
          "& .MuiDrawer-paper": {
            width: "100%",
            maxWidth: "800px",
            boxSizing: "border-box",
            px: 3,
            py: 4,
          },
        }}
      >
        {!!selectedClient && (
          <Stack role="presentation" spacing={3} divider={<Divider />}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <User
                email={selectedClient.username}
                name={formatName(selectedClient)}
              />
              <IconButton onClick={handleCloseDrawer}>
                <Icon name="Close" />
              </IconButton>
            </Stack>

            {!getUpcomingSessions(selectedClient)?.length ? null : (
              <Box>
                <ScheduledSessions
                  type="coach"
                  name={selectedClient.username}
                  data={getUpcomingSessions(selectedClient)}
                />
              </Box>
            )}
            <ClientNotes
              username={selectedClient.username}
              unsavedRef={unsavedRef}
            />
          </Stack>
        )}
      </Drawer>
    </>
  );
}
